import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { SERVER_BASE_URL } from '../utils/api';

const SuccessContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding-top: 100px;
  padding-bottom: 60px;
`;

const SuccessContent = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SuccessCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 40px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  text-align: center;
`;

const SuccessIcon = styled.div`
  font-size: 64px;
  margin-bottom: 20px;
`;

const SuccessTitle = styled.h1`
  font-size: 32px;
  color: #2e7d32;
  margin-bottom: 15px;
`;

const SuccessMessage = styled.p`
  color: #666;
  font-size: 18px;
  margin-bottom: 30px;
  line-height: 1.6;
`;

const OrderInfo = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 30px;
`;

const OrderInfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  
  &:last-child {
    margin-bottom: 0;
    border-top: 1px solid #ddd;
    padding-top: 10px;
    font-weight: 600;
  }
`;

const OrderLabel = styled.span`
  color: #666;
`;

const OrderValue = styled.span`
  color: #333;
  font-weight: ${props => props.bold ? '600' : '400'};
`;

const Button = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 15px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 15px;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
  }
`;

const SecondaryButton = styled.button`
  width: 100%;
  background: transparent;
  color: #ff6b35;
  border: 2px solid #ff6b35;
  border-radius: 8px;
  padding: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #ff6b35;
    color: white;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
`;

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const [orderInfo, setOrderInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Проверяем авторизацию
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    const token = localStorage.getItem('token');
    
    // Пытаемся получить информацию о заказе из localStorage
    const pendingOrder = localStorage.getItem('pendingOrder');
    let orderIdFromStorage = null;
    if (pendingOrder) {
      try {
        const order = JSON.parse(pendingOrder);
        orderIdFromStorage = order.orderId;
        setOrderInfo(order);
      } catch (error) {
        console.error('Ошибка парсинга данных заказа:', error);
      }
    }

    // Также проверяем параметры URL (если Robokassa передает их)
    const invId = searchParams.get('InvId');
    const outSum = searchParams.get('OutSum');
    
    // Используем ID заказа из URL или из localStorage
    const orderId = invId ? parseInt(invId) : orderIdFromStorage;
    
    // Если есть ID заказа, проверяем и обновляем статус
    if (orderId) {
      // Загружаем информацию о заказе из API для проверки реального статуса
      fetch(`${SERVER_BASE_URL}/api/orders/robokassa/status/${orderId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      .then(response => response.json())
      .then(data => {
        if (data.success && data.data) {
          const paymentStatus = data.data.paymentStatus;
          
          // Если статус не оплачен, но мы на странице успешной оплаты, обновляем статус
          if (paymentStatus !== 'paid') {
            console.log('Статус заказа не оплачен, но мы на странице успешной оплаты. Обновляем статус...');
            // Пытаемся обновить статус через fallback механизм
            fetch(`${SERVER_BASE_URL}/api/orders/robokassa/confirm/${orderId}`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            })
            .then(confirmResponse => confirmResponse.json())
            .then(confirmData => {
              if (confirmData.success) {
                console.log('✅ Статус заказа успешно обновлен через fallback механизм');
                // Очищаем pendingOrder после успешного подтверждения
                localStorage.removeItem('pendingOrder');
                setOrderInfo({
                  orderId: confirmData.data.orderId,
                  orderNumber: confirmData.data.orderNumber,
                  amount: data.data.totalAmount,
                  subtotal: data.data.subtotal,
                  discountAmount: data.data.discountAmount,
                  discountReason: data.data.discountReason,
                  pointsSpent: data.data.pointsSpent,
                  deliveryCost: data.data.deliveryCost,
                  paymentStatus: 'paid'
                });
              } else {
                setOrderInfo({
                  orderId: data.data.orderId,
                  orderNumber: data.data.orderNumber,
                  amount: data.data.totalAmount,
                  subtotal: data.data.subtotal,
                  discountAmount: data.data.discountAmount,
                  discountReason: data.data.discountReason,
                  pointsSpent: data.data.pointsSpent,
                  deliveryCost: data.data.deliveryCost,
                  paymentStatus: paymentStatus
                });
              }
              setLoading(false);
            })
            .catch(confirmError => {
              console.error('Ошибка обновления статуса заказа:', confirmError);
              setOrderInfo({
                orderId: data.data.orderId,
                orderNumber: data.data.orderNumber,
                amount: data.data.totalAmount,
                subtotal: data.data.subtotal,
                discountAmount: data.data.discountAmount,
                discountReason: data.data.discountReason,
                pointsSpent: data.data.pointsSpent,
                deliveryCost: data.data.deliveryCost,
                paymentStatus: paymentStatus
              });
              setLoading(false);
            });
          } else {
            // Статус уже оплачен
            // Очищаем pendingOrder
            localStorage.removeItem('pendingOrder');
            setOrderInfo({
              orderId: data.data.orderId,
              orderNumber: data.data.orderNumber,
              amount: data.data.totalAmount,
              subtotal: data.data.subtotal,
              discountAmount: data.data.discountAmount,
              discountReason: data.data.discountReason,
              pointsSpent: data.data.pointsSpent,
              deliveryCost: data.data.deliveryCost,
              paymentStatus: paymentStatus
            });
            setLoading(false);
          }
        } else {
          // Fallback на параметры URL или localStorage
          if (outSum) {
            setOrderInfo({
              orderId: orderId,
              orderNumber: orderIdFromStorage ? `ORD-${orderId}` : null,
              amount: parseFloat(outSum)
            });
          }
          setLoading(false);
        }
      })
      .catch(error => {
        console.error('Ошибка загрузки статуса заказа:', error);
        // Fallback на параметры URL или localStorage
        if (outSum) {
          setOrderInfo({
            orderId: orderId,
            orderNumber: orderIdFromStorage ? `ORD-${orderId}` : null,
            amount: parseFloat(outSum)
          });
        }
        setLoading(false);
      });
    } else if (outSum) {
      // Если нет ID, используем только сумму
      setOrderInfo(prevOrderInfo => {
        if (!prevOrderInfo) {
          return {
            orderId: null,
            orderNumber: null,
            amount: parseFloat(outSum)
          };
        }
        return prevOrderInfo;
      });
      setLoading(false);
    } else {
      // Если нет ни orderId, ни outSum, но есть pendingOrder в localStorage, попробуем использовать его
      if (pendingOrder && orderIdFromStorage) {
        // Повторяем попытку с orderIdFromStorage
        const orderIdRetry = orderIdFromStorage;
        fetch(`${SERVER_BASE_URL}/api/orders/robokassa/status/${orderIdRetry}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
        .then(response => response.json())
        .then(data => {
          if (data.success && data.data) {
            const paymentStatus = data.data.paymentStatus;
            if (paymentStatus !== 'paid') {
              console.log('Статус заказа не оплачен, но мы на странице успешной оплаты. Обновляем статус...');
              fetch(`${SERVER_BASE_URL}/api/orders/robokassa/confirm/${orderIdRetry}`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              })
              .then(confirmResponse => confirmResponse.json())
              .then(confirmData => {
                if (confirmData.success) {
                  console.log('✅ Статус заказа успешно обновлен через fallback механизм');
                  localStorage.removeItem('pendingOrder');
                  setOrderInfo({
                    orderId: confirmData.data.orderId,
                    orderNumber: confirmData.data.orderNumber,
                    amount: data.data.totalAmount,
                    subtotal: data.data.subtotal,
                    discountAmount: data.data.discountAmount,
                    discountReason: data.data.discountReason,
                    pointsSpent: data.data.pointsSpent,
                    deliveryCost: data.data.deliveryCost,
                    paymentStatus: 'paid'
                  });
                }
                setLoading(false);
              })
              .catch(confirmError => {
                console.error('Ошибка обновления статуса заказа:', confirmError);
                setLoading(false);
              });
            } else {
              localStorage.removeItem('pendingOrder');
              setOrderInfo({
                orderId: data.data.orderId,
                orderNumber: data.data.orderNumber,
                amount: data.data.totalAmount,
                subtotal: data.data.subtotal,
                discountAmount: data.data.discountAmount,
                discountReason: data.data.discountReason,
                pointsSpent: data.data.pointsSpent,
                deliveryCost: data.data.deliveryCost,
                paymentStatus: paymentStatus
              });
              setLoading(false);
            }
          } else {
            setLoading(false);
          }
        })
        .catch(error => {
          console.error('Ошибка загрузки статуса заказа:', error);
          setLoading(false);
        });
      } else {
        setLoading(false);
      }
    }
  }, [navigate, isAuthenticated, searchParams]);

  const handleGoHome = () => {
    navigate('/');
  };

  const handleViewOrders = () => {
    navigate('/dashboard/orders-history');
  };

  if (loading) {
    return (
      <SuccessContainer>
        <SuccessContent>
          <LoadingMessage>Загрузка...</LoadingMessage>
        </SuccessContent>
      </SuccessContainer>
    );
  }

  return (
    <SuccessContainer>
      <SuccessContent>
        <SuccessCard>
          <SuccessIcon>✅</SuccessIcon>
          <SuccessTitle>Оплата успешно завершена!</SuccessTitle>
          <SuccessMessage>
            Ваш заказ принят в обработку. Мы свяжемся с вами для подтверждения деталей доставки.
          </SuccessMessage>

          {orderInfo && (
            <OrderInfo>
              <OrderInfoRow>
                <OrderLabel>Номер заказа:</OrderLabel>
                <OrderValue>{orderInfo.orderNumber || `#${orderInfo.orderId}`}</OrderValue>
              </OrderInfoRow>
              {orderInfo.subtotal > 0 && (
                <OrderInfoRow>
                  <OrderLabel>Товары:</OrderLabel>
                  <OrderValue>{orderInfo.subtotal?.toLocaleString()} ₽</OrderValue>
                </OrderInfoRow>
              )}
              {orderInfo.discountAmount > 0 && (
                <OrderInfoRow>
                  <OrderLabel>
                    Скидка{orderInfo.discountReason && orderInfo.discountReason.match(/(\d+)%/) ? ` ${orderInfo.discountReason.match(/(\d+)%/)[1]}%` : ''}:
                  </OrderLabel>
                  <OrderValue style={{ color: '#2e7d32' }}>-{orderInfo.discountAmount?.toLocaleString()} ₽</OrderValue>
                </OrderInfoRow>
              )}
              {orderInfo.pointsSpent > 0 && (
                <OrderInfoRow>
                  <OrderLabel>Списано баллов:</OrderLabel>
                  <OrderValue style={{ color: '#ff6b35' }}>-{orderInfo.pointsSpent}</OrderValue>
                </OrderInfoRow>
              )}
              {orderInfo.deliveryCost > 0 && (
                <OrderInfoRow>
                  <OrderLabel>Доставка:</OrderLabel>
                  <OrderValue>{orderInfo.deliveryCost?.toLocaleString()} ₽</OrderValue>
                </OrderInfoRow>
              )}
              {orderInfo.deliveryCost === 0 && orderInfo.subtotal > 0 && (
                <OrderInfoRow>
                  <OrderLabel>Доставка:</OrderLabel>
                  <OrderValue style={{ color: '#28a745' }}>Бесплатно</OrderValue>
                </OrderInfoRow>
              )}
              <OrderInfoRow>
                <OrderLabel>Сумма оплаты:</OrderLabel>
                <OrderValue bold>{orderInfo.amount?.toLocaleString()} ₽</OrderValue>
              </OrderInfoRow>
            </OrderInfo>
          )}

          <div style={{ fontSize: '14px', color: '#666', marginBottom: '30px' }}>
            На ваш email будет отправлено подтверждение заказа
          </div>

          <Button onClick={handleViewOrders}>
            Посмотреть мои заказы
          </Button>
          <SecondaryButton onClick={handleGoHome}>
            Вернуться на главную
          </SecondaryButton>
        </SuccessCard>
      </SuccessContent>
    </SuccessContainer>
  );
};

export default PaymentSuccess;




